import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-8">
      <div className="w-full max-w-sm ch-fade-up">
        <div className="mb-10"><Logo /></div>
        {sent ? (
          <div className="space-y-4">
            <MailCheck className="h-10 w-10" strokeWidth={1.5} />
            <h2 className="font-display text-3xl font-bold tracking-tight">{t("forgot.sent_title")}</h2>
            <p className="text-sm text-neutral-500">{t("forgot.sent_body")}</p>
            <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-sm font-medium hover:underline">
              <ArrowLeft className="h-4 w-4" /> {t("common.back_login")}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("forgot.eyebrow")}</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">{t("forgot.title")}</h2>
            <p className="mt-2 text-sm text-neutral-500">{t("forgot.subtitle")}</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">{t("common.email")}</Label>
                <Input id="email" data-testid="forgot-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-sm" />
              </div>
              <Button type="submit" data-testid="forgot-submit-button" disabled={loading}
                className="h-11 w-full rounded-sm bg-black text-white hover:bg-neutral-800">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("forgot.submit")}
              </Button>
              <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black">
                <ArrowLeft className="h-4 w-4" /> {t("common.back_login")}
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
